import React, { useState, useRef, useEffect, useCallback } from "react"
import { FormattedMessage } from "gatsby-plugin-intl"
import { Button, CustomModal } from "@tmu/components/common"
import { StyledModalActions } from "@tmu/global/page-addons/dashboard.styles"
import ReactCrop, {
  centerCrop,
  makeAspectCrop,
  convertToPixelCrop,
} from "react-image-crop"
import { base64StringtoFile } from "@tmu/utils/image"
import { modalStyles } from "@tmu/global/GlobalStyle"
import "react-image-crop/dist/ReactCrop.css"
import styled from "@emotion/styled"

const StyledCropContainer = styled.div`
  .ReactCrop {
    position: relative;
    display: inline-block;
    cursor: crosshair;
    overflow: hidden;
    max-width: 100%;
    background-color: #fff;
  }

  .ReactCrop:focus {
    outline: none;
  }

  .ReactCrop--disabled {
    cursor: inherit;
  }

  .ReactCrop__image {
    display: block;
    max-width: 100%;
    touch-action: none;
  }

  .ReactCrop__crop-selection {
    position: absolute;
    top: 0;
    left: 0;
    transform: translate3d(0, 0, 0);
    box-sizing: border-box;
    cursor: move;
    box-shadow: 0 0 0 9999px rgba(0, 0, 0, 0.5);
    border: 1px solid #fff;
    touch-action: none;
  }

  .ReactCrop__drag-handle {
    position: absolute;
    width: 10px;
    height: 10px;
    background-color: rgba(255, 255, 255, 0.75);
    border: 1px solid #000;
    box-sizing: border-box;
  }
`

function centerAspectCrop(mediaWidth, mediaHeight, aspect) {
  return centerCrop(
    makeAspectCrop(
      {
        unit: "%",
        width: 90,
      },
      aspect,
      mediaWidth,
      mediaHeight
    ),
    mediaWidth,
    mediaHeight
  )
}

const ImageCropper = ({
  isOpen,
  image,
  id,
  onConfirm,
  onCancel,
  square,
  banner,
}) => {
  const imgRef = useRef(null)
  const [imgSrc, setImgSrc] = useState(null)
  const [crop, setCrop] = useState()
  const [completedCrop, setCompletedCrop] = useState()
  const aspect = square ? 1 : banner ? 4 : 16 / 9

  // Memoize the image loading logic
  const loadImage = useCallback(() => {
    if (!image) {
      setImgSrc(null)
      return
    }

    if (typeof image === "string" && image.startsWith("data:")) {
      setImgSrc(image)
      return
    }

    if (image instanceof File || image instanceof Blob) {
      const reader = new FileReader()
      reader.onload = () => {
        if (typeof reader.result === "string") {
          setImgSrc(reader.result)
        }
      }
      reader.readAsDataURL(image)
    }
  }, [image])

  // Only load image when component is open
  useEffect(() => {
    if (isOpen) {
      loadImage()
    } else {
      // Reset states when modal closes
      setImgSrc(null)
      setCrop(undefined)
      setCompletedCrop(undefined)
    }
  }, [isOpen, loadImage])

  const onImageLoad = useCallback(
    (e) => {
      if (!e?.currentTarget) return

      const { width, height } = e.currentTarget
      const newCrop = centerAspectCrop(width, height, aspect)
      setCrop(newCrop)
      const pixelCrop = convertToPixelCrop(newCrop, width, height)
      setCompletedCrop(pixelCrop)
    },
    [aspect]
  )

  async function handleConfirm() {
    console.log("Confirming crop")
    const image = imgRef.current
    if (!image || !completedCrop) {
      console.error("Missing image or crop data:", { image, completedCrop })
      throw new Error("Image or crop does not exist")
    }

    // Log original image size
    console.log("Original image size:", {
      width: image.naturalWidth,
      height: image.naturalHeight,
    })

    const scaleX = image.naturalWidth / image.width
    const scaleY = image.naturalHeight / image.height

    const canvas = document.createElement("canvas")
    canvas.width = completedCrop.width * scaleX
    canvas.height = completedCrop.height * scaleY

    // Log cropped size before resizing
    console.log("Cropped size before resizing:", {
      width: canvas.width,
      height: canvas.height,
      aspectRatio: (canvas.width / canvas.height).toFixed(2),
      comparison:
        canvas.width > canvas.height ? "Width is longer" : "Height is longer",
    })

    const ctx = canvas.getContext("2d")
    if (!ctx) {
      console.error("Failed to get canvas context")
      throw new Error("No 2d context")
    }

    ctx.drawImage(
      image,
      completedCrop.x * scaleX,
      completedCrop.y * scaleY,
      completedCrop.width * scaleX,
      completedCrop.height * scaleY,
      0,
      0,
      canvas.width,
      canvas.height
    )

    // Create a new canvas for resizing
    const resizedCanvas = document.createElement("canvas")
    const resizedCtx = resizedCanvas.getContext("2d")

    // Calculate new dimensions while maintaining aspect ratio
    let newWidth = canvas.width
    let newHeight = canvas.height

    if (newWidth > 1200) {
      newHeight = (newHeight * 1200) / newWidth
      newWidth = 1200
    }

    if (newHeight > 800) {
      newWidth = (newWidth * 800) / newHeight
      newHeight = 800
    }

    // Set the resized canvas dimensions
    resizedCanvas.width = newWidth
    resizedCanvas.height = newHeight

    // Log final resized dimensions
    console.log("Final resized dimensions:", {
      width: newWidth,
      height: newHeight,
      aspectRatio: (newWidth / newHeight).toFixed(2),
      comparison: newWidth > newHeight ? "Width is longer" : "Height is longer",
    })

    // Draw the image on the resized canvas
    resizedCtx.drawImage(canvas, 0, 0, newWidth, newHeight)

    // Function to compress image to under 1MB
    const compressImage = async (canvas, quality = 0.9) => {
      return new Promise((resolve) => {
        canvas.toBlob(
          (blob) => {
            if (blob.size > 1000000 && quality > 0.1) {
              // If over 1MB and quality still above 0.1
              // Reduce quality and try again
              compressImage(canvas, quality - 0.1).then(resolve)
            } else {
              console.log("Final image size:", {
                size: blob.size,
                sizeInMB: (blob.size / 1000000).toFixed(2) + "MB",
                quality: quality,
              })
              resolve(blob)
            }
          },
          "image/jpeg",
          quality
        )
      })
    }

    const blob = await compressImage(resizedCanvas)
    if (!blob) {
      console.error("Failed to create blob from canvas")
      throw new Error("Failed to create blob")
    }

    // Convert blob to base64
    const reader = new FileReader()
    reader.readAsDataURL(blob)
    reader.onloadend = () => {
      const base64data = reader.result
      if (!base64data) {
        console.error("Failed to read blob as data URL")
        return
      }
      const newCroppedFile = base64StringtoFile(base64data, "image.png")
      onConfirm({ result: newCroppedFile, id })
    }
  }

  return (
    <CustomModal
      isOpen={isOpen}
      style={modalStyles}
      ariaHideApp={false}
      shouldCloseOnOverlayClick={false}
      onRequestClose={onCancel}>
      <StyledCropContainer>
        <div
          style={{
            maxWidth: "100%",
            maxHeight: "80vh",
            overflow: "hidden",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            padding: "20px",
            backgroundColor: "#f5f5f5",
          }}>
          {!!imgSrc && (
            <div
              style={{ position: "relative", width: "100%", height: "100%" }}>
              <ReactCrop
                crop={crop}
                onChange={(_, percentCrop) => setCrop(percentCrop)}
                onComplete={(c) => setCompletedCrop(c)}
                aspect={aspect}
                minHeight={100}
                className="ReactCrop--fixed"
                style={{
                  width: "100%",
                  height: "100%",
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                }}>
                <img
                  ref={imgRef}
                  alt="Crop me"
                  src={imgSrc}
                  onLoad={onImageLoad}
                  style={{
                    maxWidth: "100%",
                    maxHeight: "60vh",
                    objectFit: "contain",
                    display: "block",
                  }}
                />
              </ReactCrop>
            </div>
          )}
        </div>
      </StyledCropContainer>

      <StyledModalActions>
        <Button
          color="red"
          label="done"
          onClick={handleConfirm}
          disabled={!completedCrop}>
          <FormattedMessage id="dashboard::forms::crop" defaultMessage="Crop" />
        </Button>
        <Button variant="outlined" label="cancel" onClick={onCancel}>
          <FormattedMessage
            id="dashboard::forms::close"
            defaultMessage="Close"
          />
        </Button>
      </StyledModalActions>
    </CustomModal>
  )
}

export default ImageCropper
